import MainLayout from "@/layout/MainLayout/MainLayout";
import App, { AppContext, AppProps } from "next/app";
import { Roboto } from "next/font/google";
import React, { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import { ClientConfigProvider } from "@/context/ConfigContext";
import { Config } from "@/types/Config";
import { Provider, useDispatch } from "react-redux";
import { persistor, store } from "@/redux/store";
import { PersistGate } from "redux-persist/integration/react";
import "../styles/globals.css";
import { IntlProvider } from "next-intl";
import { parseCookies, setCookie } from "nookies";
import jwt from "jsonwebtoken";
import { SessionExpirationProvider } from "@/context/SessionExprationContext";
import Head from "next/head";
import { AuthProvider } from "@/context/AuthContext";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import LoadingSpinner from "@/components/layout/LoadingSpinner/LoadingSpinner";

import { LocalizationProvider } from "@/context/LocalizationContext";
import { DefaultSeo } from "next-seo";
import { ScrollProvider } from "@/context/ScrollContext";
import { useUserJourney } from "@/hooks/useUserJourney";

const roboto = Roboto({
  weight: ["400", "500", "700", "900"],
  subsets: ["latin"],
  display: "swap",
});

interface MyAppProps extends AppProps {
  config: Config;
  messages: any;
}

function MyApp({ Component, pageProps, config, messages }: MyAppProps) {
  const { locale = "en" } = useRouter();
  const [clientMessages, setClientMessages] = useState(messages);
  const queryClient = new QueryClient();
  const router = useRouter();
  const [loadingPage, setLoading] = useState(false);
  const prevPathRef = useRef<string>(router.asPath);

  useEffect(() => {
    if (!locale) return;

    async function loadMessages() {
      try {
        const localeMessages = await import(`@/locales/${locale}.json`);
        setClientMessages(localeMessages.default);
      } catch (error) {
        console.error(`Error loading locale messages for ${locale}:`, error);
      }
    }

    loadMessages();
    // const messages = localesMessages[locale];
    // if (messages) {
    //   setClientMessages(messages as Record<string, string>);
    // } else {
    //   import(`@/locales/${locale}.json`)
    //     .then((mod) => setClientMessages(mod.default as Record<string, string>))
    //     .catch((err) => console.error("Dynamic locale import failed", err));
    // }

    document.documentElement.setAttribute(
      "dir",
      locale === "ar" ? "rtl" : "ltr"
    );
  }, [locale]);

  useEffect(() => {
    const handleStart = (url: string) => {
      const currentUrl = prevPathRef.current || "";
      if (typeof currentUrl !== "string" || typeof url !== "string") return;

      const currentPath = currentUrl?.split?.("?")[0] || "";
      const nextPath = url?.split?.("?")[0] || "";

      const isHomeToSearch =
        (currentPath === "/" ||
          currentPath === "/home" ||
          currentPath === "/ar/home") &&
        (nextPath === "/search" || nextPath === "/ar/search");
      const isSameExactUrl = currentUrl === url;
      const isSameSearchRoute =
        (url.startsWith("/search?sid=") &&
          currentUrl.startsWith("/search?sid=")) ||
        (url.startsWith("/ar/search?sid=") &&
          currentUrl.startsWith("/ar/search?sid="));
      const isSameSearchHotelRoute =
        (url.startsWith("/search/hotel/") &&
          currentUrl.startsWith("/search/hotel/")) ||
        (url.startsWith("/ar/search/hotel/") &&
          currentUrl.startsWith("/ar/search/hotel/"));
 const isSameFlightSearchRoute =
        (url.startsWith("/flights?sid=") &&
          currentUrl.startsWith("/flights?sid=")) ||
        (url.startsWith("/ar/flights?sid=") &&
          currentUrl.startsWith("/ar/flights?sid="));


      const disabledPaths = [
        "/signin",
        "/signup",
        "/login-confirm",
        "/reset-password",
        "/confirm-password",
        "/verify-password",
        "/sessionExpired",
        "/terms-and-conditions",
        "/privacy",
        "/ar/signin",
        "/ar/signup",
        "/ar/login-confirm",
        "/ar/reset-password",
        "/ar/confirm-password",
        "/ar/verify-password",
        "/ar/sessionExpired",
        "/ar/terms-and-conditions",
        "/ar/privacy",
      ];

      if (
        disabledPaths.includes(currentPath || "") ||
        disabledPaths.includes(nextPath)
      ) {
        return;
      }

      if (
        isSameSearchRoute ||
        isHomeToSearch ||
        isSameExactUrl ||
        isSameSearchHotelRoute||
        isSameFlightSearchRoute
      ) {
        return;
      }

      setLoading(true);
    };
    const handleComplete = (url: string) => {
      setLoading(false);
      prevPathRef.current = url;
    };

    router.events.on("routeChangeStart", handleStart);
    router.events.on("routeChangeComplete", handleComplete);
    router.events.on("routeChangeError", handleComplete);

    return () => {
      router.events.off("routeChangeStart", handleStart);
      router.events.off("routeChangeComplete", handleComplete);
      router.events.off("routeChangeError", handleComplete);
    };
  }, [router]);

  const { asPath } = useRouter();
  const canonicalUrl = `${process.env.NEXT_PUBLIC_SITE_URL}${
    asPath.split("?")[0]
  }`;

  const { trackEvent } = useUserJourney("1231231211");

  return (
    <React.Fragment>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title> Almuayed: Hotels and Flights</title>

        <meta name="robots" content="index, follow" />
        <meta
          name="description"
          content="Almuayed | Flights & Hotels in Saudi Arabia & the Gulf
                Book flights and hotels easily with Almuayed across Saudi Arabia and the Gulf. Find the best prices, secure payments, and 24/7 support for a seamless travel experience"
        />
        <link rel="canonical" href={canonicalUrl} />

        <meta
          name="keywords"
          content="Almuayed hotels, hotel booking, book hotels online, Almuayed travel, luxury hotels, budget hotels, hotel deals"
        />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Almuayed" />
        <meta property="og:title" content="Almuayed: Hotels and Flights" />
        <meta
          property="og:description"
          content="Almuayed: Book flights & hotels easily across Saudi Arabia & the Gulf. Get the best prices, secure payments, & 24/7 support for a seamless experience."
        />
        <meta property="og:url" content={process.env.NEXT_PUBLIC_SITE_URL} />

        <meta
          property="og:image"
          content={`${process.env.NEXT_PUBLIC_SITE_URL}/logo.png`}
        />

        <link rel="icon" href="/favicon.ico" />
        <meta charSet="UTF-8" />
        <meta
          name="google-site-verification"
          content="MBUvKrMyLTPX1SUCwq09JcYQWuFbA_xp8xq4hGve-W4"
        />
        <meta property="og:type" content="website" />
        <link rel="icon" href="/favicon.ico" />
        <meta charSet="UTF-8" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Organization",
              name: "Almuayed",
              url: `${process.env.NEXT_PUBLIC_SITE_URL}`,
              logo: `${process.env.NEXT_PUBLIC_SITE_URL}/logo.png`,
            }),
          }}
        />
      </Head>

      <DefaultSeo
        title="Almuayed: Hotels and Flights"
        description="Almuayed | Flights & Hotels in Saudi Arabia & the Gulf Book flights and hotels easily with Almuayed across Saudi Arabia and the Gulf. Find the best prices, secure payments, and 24/7 support for a seamless travel experience"
        openGraph={{
          type: "website",
          locale: "en_IE",
          url: `${process.env.NEXT_PUBLIC_SITE_URL}`,
          site_name: "Almuayed",
        }}
      />

      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <LocalizationProvider>
            <ClientConfigProvider initialConfig={config}>
              <QueryClientProvider client={queryClient}>
                <IntlProvider messages={clientMessages || {}} locale={locale}>
                  <SessionExpirationProvider>
                    <AuthProvider>
                      <ScrollProvider>
                        <div id="app-root" className={`ltr:${roboto.className}`}>
                          <MainLayout>
                            {loadingPage && <LoadingSpinner />}
                            <Component {...pageProps} trackEvent={trackEvent} />
                          </MainLayout>
                        </div>
                      </ScrollProvider>
                    </AuthProvider>
                  </SessionExpirationProvider>
                </IntlProvider>
              </QueryClientProvider>
            </ClientConfigProvider>
          </LocalizationProvider>
        </PersistGate>
      </Provider>
    </React.Fragment>
  );
}

MyApp.getInitialProps = async (appContext: AppContext) => {
  const { ctx } = appContext;
  const { locale = "en" } = ctx;

  const appProps = await App.getInitialProps(appContext);
  let clientConfig = {};
  let messages: Record<string, string> = {};

  try {
    clientConfig = (await import("@/data/config.json")).default;
    messages = (await import(`@/locales/${locale}.json`)).default;
  } catch (error) {
    console.error(`Error loading locale messages for ${locale}:`, error);
  }

  if (typeof window === "undefined") {
    try {
      const cookies = parseCookies(ctx);
      const hasSession = cookies["next-auth.session-token"];
      if (!hasSession) {
        const response = await fetch(
          `${process.env.AUTH_BASE_URL}/client/login-pw`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            redirect: "manual",
            body: JSON.stringify({
              acmid: process.env.NEXT_PUBLIC_Almuayed_ACMID,
              email: process.env.NEXT_PUBLIC_DEFAULT_USER_AUTH_USERNAME,
              password: process.env.NEXT_PUBLIC_DEFAULT_USER_AUTH_PASSWORD,
            }),
          }
        );

        if (response.status === 307 || response.status === 308) {
          console.warn("Redirected to:", response.headers.get("location"));
          throw new Error("Unexpected redirect during guest login");
        }

        if (!response.ok)
          throw new Error("Failed to fetch login default token");

        const data = await response.json();
        if (data.success && ctx.res) {
          const secret = process.env.NEXT_PUBLIC_JWT_SECRET;
          if (!secret) throw new Error("JWT_SECRET is missing");
          const token = jwt.sign(
            { user_type: "guest", accessToken: data.access_token },
            secret
          );
          setCookie(ctx, "next-auth.guest-token", token, { path: "/" });
        }
      }
    } catch (error) {
      console.error("Error during guest login:", error);
    }
  }

  return {
    ...appProps,
    config: clientConfig,
    messages,
  };
};

export default MyApp;
